import { useEffect, useState } from "react";
import { ArrowUpRight, Instagram, Menu, Play, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { APP_STORE_URL, INSTAGRAM_URL, NAV_LINKS, PLAY_STORE_URL } from "@/constants/site";

export const Navbar = () => {
  const location = useLocation();
  const onBlog = location.pathname.startsWith("/blog");
  const anchorPrefix = location.pathname === "/" ? "" : "/";
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      setProgress(max > 0 ? (el.scrollTop / max) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_LINKS.map((l) => document.getElementById(l.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <div
        data-testid="scroll-progress"
        role="progressbar"
        aria-label="Page scroll progress"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="fixed inset-x-0 top-0 z-[60] h-[3px] bg-gradient-to-r from-splurge-dark via-splurge-primary to-splurge-violet transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
      <header className="sticky top-0 z-50 border-b border-splurge-lavender/80 bg-white/85 backdrop-blur-xl">
        <nav
          aria-label="Main navigation"
          className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 sm:px-8"
        >
          <Link
            to="/"
            data-testid="nav-logo"
            aria-label="Splurge Finance home"
            className="flex items-center gap-2.5"
          >
            <img
              src="/logo.png"
              alt="Splurge Finance"
              className="h-12 w-12 rounded-full shadow-md shadow-splurge-primary/25 ring-1 ring-splurge-lavender sm:h-14 sm:w-14"
            />
          </Link>

          <ul className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((link) => (
              <li key={link.id}>
                <Link
                  to={`${anchorPrefix}#${link.id}`}
                  data-testid={`nav-link-${link.id}`}
                  aria-current={active === link.id ? "true" : undefined}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                    active === link.id && !onBlog
                      ? "bg-splurge-lavender text-splurge-dark"
                      : "text-splurge-ink/70 hover:bg-splurge-lavender/60 hover:text-splurge-ink"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/blog"
                data-testid="nav-link-blog"
                aria-current={onBlog ? "page" : undefined}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  onBlog
                    ? "bg-splurge-lavender text-splurge-dark"
                    : "text-splurge-ink/70 hover:bg-splurge-lavender/60 hover:text-splurge-ink"
                }`}
              >
                Blog
              </Link>
            </li>
          </ul>

          <div className="flex items-center gap-3">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              aria-label="Splurge Finance on Instagram (opens in a new tab)"
              className="hidden h-10 w-10 place-items-center rounded-full border border-splurge-lavender text-splurge-ink/70 transition-all duration-200 hover:border-splurge-primary/40 hover:text-splurge-primary active:scale-95 sm:grid"
            >
              <Instagram className="h-[18px] w-[18px]" aria-hidden="true" />
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-google-play"
              aria-label="Get Splurge on Google Play (opens in a new tab)"
              className="hidden h-10 w-10 place-items-center rounded-full border border-splurge-lavender text-splurge-ink/70 transition-all duration-200 hover:border-splurge-primary/40 hover:text-splurge-primary active:scale-95 sm:grid"
            >
              <Play className="h-[18px] w-[18px]" aria-hidden="true" />
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta"
              aria-label="Get the Splurge app on the App Store (opens in a new tab)"
              className="hidden items-center gap-1.5 rounded-full bg-splurge-primary px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-splurge-primary/30 transition-all duration-200 hover:bg-splurge-dark hover:shadow-splurge-dark/30 active:scale-95 sm:inline-flex"
            >
              Get the app
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? "Close menu" : "Open menu"}
              onClick={() => setOpen((v) => !v)}
              className="grid h-10 w-10 place-items-center rounded-xl border border-splurge-lavender text-splurge-ink transition-colors hover:bg-splurge-lavender lg:hidden"
            >
              {open ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            id="mobile-menu"
            data-testid="mobile-menu"
            className="border-t border-splurge-lavender bg-white px-5 pb-6 pt-3 lg:hidden"
          >
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  <Link
                    to={`${anchorPrefix}#${link.id}`}
                    data-testid={`mobile-nav-link-${link.id}`}
                    onClick={() => setOpen(false)}
                    className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                      active === link.id && !onBlog
                        ? "bg-splurge-lavender text-splurge-dark"
                        : "text-splurge-ink/80 hover:bg-splurge-lavender/60"
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/blog"
                  data-testid="mobile-nav-link-blog"
                  onClick={() => setOpen(false)}
                  className={`block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                    onBlog
                      ? "bg-splurge-lavender text-splurge-dark"
                      : "text-splurge-ink/80 hover:bg-splurge-lavender/60"
                  }`}
                >
                  Blog
                </Link>
              </li>
              <li className="pt-2">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-cta"
                  aria-label="Get the Splurge app on the App Store (opens in a new tab)"
                  className="flex items-center justify-center gap-1.5 rounded-full bg-splurge-primary px-5 py-3 text-sm font-bold text-white shadow-lg shadow-splurge-primary/30 active:scale-95"
                >
                  Get the app
                  <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-google-play"
                  aria-label="Get the Splurge app on Google Play (opens in a new tab)"
                  className="flex items-center justify-center gap-1.5 rounded-full border border-splurge-ink/12 bg-white px-5 py-3 text-sm font-bold text-splurge-ink active:scale-95"
                >
                  <Play className="h-4 w-4" aria-hidden="true" />
                  Get it on Google Play
                </a>
              </li>
            </ul>
          </div>
        )}
      </header>
    </>
  );
};
